import {
  DEVICE_HISTORY_EVENT_VALUES,
  DEVICE_STATUS_VALUES,
  SHIPMENT_KIND_VALUES,
} from '@/features/device-management/config/deviceManagement';
import type {
  DeviceHistoryFilters,
  DeviceListFilters,
  DeviceTransitFilters,
} from '@/features/device-management/types/deviceManagement';
import { dayKey, dayKeyToLocalDate } from '@/shared/lib/dateTime';
import type { DateRange } from 'react-day-picker';
import { z } from 'zod';

export const deviceListSearchSchema = z.object({
  q: z.string().trim().min(1).optional().catch(undefined),
  status: z.enum(DEVICE_STATUS_VALUES).optional().catch(undefined),
  org: z.string().trim().min(1).optional().catch(undefined),
});

export const deviceTransitSearchSchema = z.object({
  q: z.string().trim().min(1).optional().catch(undefined),
  shipment: z.enum(SHIPMENT_KIND_VALUES).optional().catch(undefined),
  overdue: z.literal(true).optional().catch(undefined),
  shipmentOrg: z.string().trim().min(1).optional().catch(undefined),
});

const dayKeySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => dayKey(dayKeyToLocalDate(value).getTime()) === value)
  .optional()
  .catch(undefined);

export const deviceHistorySearchSchema = z
  .object({
    q: z.string().trim().min(1).optional().catch(undefined),
    event: z.enum(DEVICE_HISTORY_EVENT_VALUES).optional().catch(undefined),
    status: z.enum(DEVICE_STATUS_VALUES).optional().catch(undefined),
    from: dayKeySchema,
    to: dayKeySchema,
  })
  .transform((search) => {
    const hasInvalidDateRange =
      (search.to !== undefined && search.from === undefined) ||
      (search.from !== undefined && search.to !== undefined && search.to < search.from);
    return hasInvalidDateRange ? { ...search, from: undefined, to: undefined } : search;
  });

export type DeviceListSearch = z.infer<typeof deviceListSearchSchema>;
export type DeviceTransitSearch = z.infer<typeof deviceTransitSearchSchema>;
export type DeviceHistorySearch = z.infer<typeof deviceHistorySearchSchema>;

export function normalizeDeviceManagementQuery(value: string | null | undefined) {
  return value?.trim() || undefined;
}

export function deviceFiltersFromSearch(search: DeviceListSearch): DeviceListFilters {
  return {
    status: search.status,
    organizationId: search.org,
  };
}

export function deviceFiltersToSearch(
  filters: DeviceListFilters,
): Pick<DeviceListSearch, 'status' | 'org'> {
  return {
    status: filters.status,
    org: filters.organizationId,
  };
}

export function hasDeviceFilters(filters: DeviceListFilters) {
  return Boolean(filters.status || filters.organizationId);
}

export function transitFiltersFromSearch(search: DeviceTransitSearch): DeviceTransitFilters {
  return {
    kind: search.shipment,
    overdue: search.overdue,
    organizationId: search.shipmentOrg,
  };
}

export function transitFiltersToSearch(
  filters: DeviceTransitFilters,
): Pick<DeviceTransitSearch, 'shipment' | 'overdue' | 'shipmentOrg'> {
  return {
    shipment: filters.kind,
    overdue: filters.overdue,
    shipmentOrg: filters.organizationId,
  };
}

export function hasTransitFilters(filters: DeviceTransitFilters) {
  return Boolean(filters.kind || filters.overdue || filters.organizationId);
}

export function deviceHistoryFiltersFromSearch(search: DeviceHistorySearch): DeviceHistoryFilters {
  return {
    eventType: search.event,
    status: search.status,
    from: search.from,
    to: search.to,
    q: search.q,
  };
}

export function deviceHistoryFiltersToSearch(filters: DeviceHistoryFilters): DeviceHistorySearch {
  return {
    event: filters.eventType,
    status: filters.status,
    from: filters.from,
    to: filters.to,
    q: filters.q,
  };
}

export function deviceHistoryDateRange(filters: DeviceHistoryFilters): DateRange | undefined {
  if (!filters.from) return undefined;
  return {
    from: dayKeyToLocalDate(filters.from),
    to: filters.to ? dayKeyToLocalDate(filters.to) : undefined,
  };
}

export function deviceHistoryFiltersFromDateRange(range: DateRange | undefined) {
  return {
    from: range?.from ? dayKey(range.from.getTime()) : undefined,
    to: range?.to ? dayKey(range.to.getTime()) : undefined,
  };
}

export function hasDeviceHistoryFilters(filters: DeviceHistoryFilters) {
  return Boolean(filters.eventType || filters.status || filters.from || filters.to || filters.q);
}
